import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "@/shared/api";
import type {
  CreateLeadLinkRequest,
  DeleteLinkRequest,
  GetLinksRequest,
  UpdateLinkRequest,
} from "./api.dto";
import { useMutate } from "@/shared/hooks";
import { filterObjectKeys } from "@/shared/lib/utils";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { PaginatedResponse } from "@/shared/types";
import type { LeadLinkShares, LeadLink } from "../model";
import { V2PaginatedResponse } from "@/shared/types/api.v2";

export const useCreateLink = () => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "post",
    url: "/lead-links/create",
    toast: {
      success: {
        description: "Ссылка создана.",
      },
    },
    refetchQueries: [["leadLinks"], ["brands"]],
  });

  return useMutation<
    AxiosResponse,
    AxiosError,
    CreateLeadLinkRequest,
    Response
  >({
    mutationKey: ["createLink"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useLeadLinks = (params: GetLinksRequest) => {
  return useQuery<PaginatedResponse<LeadLink>>({
    queryKey: ["leadLinks", params],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<LeadLink>>(
        `/lead-links/user/${params.userId}`,
        filterObjectKeys(params, ["userId"]),
      );

      return data;
    },
  });
};

export const useLeadLinksInf = (
  params: {
    userId: string;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: string;
    type?: string;
  },
  { enabled }: { enabled: boolean },
) => {
  return useInfiniteQuery({
    queryKey: ["leadLinks", filterObjectKeys(params)],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await apiInstance.get<V2PaginatedResponse<LeadLink>>(
        `/lead-links/user/${params.userId}`,
        {
          ...filterObjectKeys(params, ["userId"]),
          currentPage: pageParam,
        },
      );

      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = params.pageSize;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled,
  });
};

export const useArchiveLink = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Ссылка архивирована.",
      },
    },
    refetchQueries: ["leadLinks"],
  });
  return useMutation<AxiosResponse, AxiosError, DeleteLinkRequest, Response>({
    mutationKey: ["deleteLink"],
    mutationFn: (data) => {
      return apiInstance.post(`/lead-links/${data.id}/archive`);
    },
    onSuccess,
    onError,
  });
};

export const useRestoreLink = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Ссылка архивирована.",
      },
    },
    refetchQueries: ["leadLinks"],
  });
  return useMutation<AxiosResponse, AxiosError, DeleteLinkRequest, Response>({
    mutationKey: ["restoreLink"],
    mutationFn: (data) => {
      return apiInstance.post(`/lead-links/${data.id}/unarchive`);
    },
    onSuccess,
    onError,
  });
};

export const useLeadLinksShares = ({
  linkId,
  enabled,
}: {
  linkId?: string;
  enabled: boolean;
}) => {
  return useQuery<LeadLinkShares>({
    queryKey: ["leadLinksShares", linkId],
    queryFn: async () => {
      try {
        const { data } = await apiInstance.get<LeadLinkShares>(
          `/lead-links/${linkId}/shares`,
        );

        return data;
      } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch lead links shares");
      }
    },
    enabled: enabled && !!linkId,
  });
};

export const useUpdateLink = (linkId: string) => {
  const { mutationFn, onSuccess, onError } = useMutate({
    method: "patch",
    url: `/lead-links/${linkId}`,
    toast: {
      success: { title: "Ссылка успешно обновлена" },
      error: { title: "Ошибка при обновлении ссылки" },
    },
    refetchQueries: ["leadLinks"],
  });

  return useMutation<AxiosResponse, AxiosError, UpdateLinkRequest>({
    mutationKey: ["updateLink", linkId],
    mutationFn,
    onSuccess,
    onError,
  });
};
